import BoldText from '@/components/BoldText';
import RegularText from '@/components/RegularText';
import { handleSaveWidgetSettings, widgetSettingSelector } from '@/redux/slice/widget.slice';
import { BlockStack, Box, Divider, InlineGrid, RangeSlider } from '@shopify/polaris';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

const sizeKeys = [
  'headingFontSize',
  'additionalMessageFontSize',
  'labelFontSize',
  'inputTitleFontSize',
  'inputFontSize',
  'errorMessageFontSize',
] as const;

type SizeKey = (typeof sizeKeys)[number];

interface FontSizeSliderProps {
  labelKey: string;
  fontSize: number;
  settingKey: SizeKey;
}

const FontSizeSlider = ({ labelKey, fontSize, settingKey }: FontSizeSliderProps) => {
  const { t } = useTranslation('widget_settings');
  const dispatch = useDispatch();
  const widgetSettings = useSelector(widgetSettingSelector);

  const handleChange = (value: number) => {
    dispatch(
      handleSaveWidgetSettings({
        sizes: {
          ...widgetSettings.sizes,
          [settingKey]: value ? Number(value) : 0,
        },
      }),
    );
  };

  return (
    <RangeSlider
      label={t(labelKey)}
      value={fontSize}
      suffix={<RegularText fontWeight="medium">{`${fontSize}px`}</RegularText>}
      step={1}
      min={1}
      max={64}
      onChange={handleChange}
    />
  );
};

export const WidgetFontSize = () => {
  const { t } = useTranslation('widget_settings');
  const widgetSettings = useSelector(widgetSettingSelector);

  const sliders: { labelKey: string; settingKey: SizeKey }[] = [
    { labelKey: 'widgetDesign.colour.size.heading', settingKey: 'headingFontSize' },
    { labelKey: 'widgetDesign.colour.size.additionalMessage', settingKey: 'additionalMessageFontSize' },
    { labelKey: 'widgetDesign.colour.size.methodLabel', settingKey: 'labelFontSize' },
    { labelKey: 'widgetDesign.colour.size.inputTitle', settingKey: 'inputTitleFontSize' },
    { labelKey: 'widgetDesign.colour.size.input', settingKey: 'inputFontSize' },
    { labelKey: 'widgetDesign.colour.size.errorMessage', settingKey: 'errorMessageFontSize' },
  ];

  return (
    <BlockStack gap={'300'}>
      <Box paddingBlockStart={'300'} paddingInline={'300'}>
        <BoldText>{t('widgetDesign.textField.fontSize.title')}</BoldText>
      </Box>

      {[0, 1, 2].map((section) => (
        <>
          <Box paddingInline={'300'} key={`section-${section}`}>
            <InlineGrid gap={'1000'} columns={{ sm: 1, md: 2 }}>
              {sliders.slice(section * 2, section * 2 + 2).map(({ labelKey, settingKey }) => (
                <Box key={settingKey}>
                  <FontSizeSlider labelKey={labelKey} fontSize={widgetSettings.sizes[settingKey]} settingKey={settingKey} />
                </Box>
              ))}
            </InlineGrid>
          </Box>
          {section < 2 && <Divider borderColor="border" />}
        </>
      ))}
    </BlockStack>
  );
};
